import { Component, computed, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges, OnDestroy, AfterViewInit, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FormField } from './dynamic-form.interface';
import { QuillModule } from 'ngx-quill';
import { FilesService } from '../../services/files.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ScheduleFieldForm } from '../schedule-field-form/schedule-field-form';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ScheduleBasicFieldForm } from '../schedule-basic-field-form/schedule-basic-field-form';
import { DatePicker } from 'primeng/datepicker';


export function maxDigits(max: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') return null;
    return control.value.toString().length > max
      ? { maxDigits: true }
      : null;
  };
}

export function minDigits(min: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') return null;
    return control.value.toString().length < min
      ? { minDigits: true }
      : null;
  };
}
@Component({
  selector: 'app-dynamic-form',
  imports: [CommonModule, ReactiveFormsModule, QuillModule, ScheduleBasicFieldForm, DatePicker],
  templateUrl: './dynamic-form.html',
  styleUrl: './dynamic-form.scss',
})
export class DynamicForm implements OnChanges, OnDestroy, AfterViewInit {
  private filesService = inject(FilesService);
  private domSanitizer = inject(DomSanitizer);

  fields = input<FormField[]>([]);
  @Input() formData: any = {};
  @Input() catalogs: any = {};
  @Input() showSubmitButton: boolean = true;
  @Input() submitButtonText: string = 'Actualizar';
  @Input() showDraftButton: boolean = false;
  @Input() draftButtonText: string = 'Guardar Borrador';
  @Input() disableSubmitWhenPristine: boolean = false;

  @Output() formSubmit = new EventEmitter<any>();
  @Output() multiselectSearch = new EventEmitter<{ fieldKey: string; query: string }>();
  @Output() multiselectCreate = new EventEmitter<{ fieldKey: string; value: string }>();
  @Output() fileDelete = new EventEmitter<{ fieldKey: string }>();
  @Output() formChange = new EventEmitter<any>();
  @Output() ready = new EventEmitter<DynamicForm>();



  form!: FormGroup;
  isLoading = signal(false);
  
  // Multiselect state
  selectedChips = signal<Record<string, any[]>>({});
  searchQuery = signal<Record<string, string>>({});
  showDropdown = signal<Record<string, boolean>>({});
  
  private searchSubject = new Subject<{ fieldKey: string; query: string }>();
  private searchSubscription: Subscription;

  // File preview state
  filePreviews = signal<Record<string, SafeUrl>>({});
  private previewsInitialized = false;

  constructor() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(500)
    ).subscribe(({ fieldKey, query }) => {
      this.multiselectSearch.emit({ fieldKey, query });
    });
  }

  ngOnInit() {
    this.form = this.createForm();
    this.initializeMultiselectValues();
    this.initializeFilePreviews();
    this.updateConditionalFields();

    this.form.valueChanges.subscribe((value) => {
      this.updateConditionalFields();
      this.formChange.emit({
        isValid: this.form.valid,
        value: this.form.value
      });
    });
  }

  isFieldVisible(field: FormField): boolean {
    if (!field.showIf) return true;
    const triggerValue = this.form?.get(field.showIf.key)?.value;
    if (field.showIf.hasValue) {
      return Array.isArray(triggerValue)
        ? triggerValue.length > 0
        : triggerValue !== null && triggerValue !== undefined && triggerValue !== '';
    }
    return Array.isArray(triggerValue)
      ? triggerValue.includes(field.showIf.value)
      : triggerValue === field.showIf.value;
  }

  private updateConditionalFields() {
    this.fields().forEach(field => {
      if (!field.showIf) return;
      const control = this.form.get(field.key);
      if (!control) return;
      const conditionMet = this.isFieldVisible(field);
      if (conditionMet && control.disabled) {
        control.enable({ emitEvent: false });
        if (field.type === 'multiselect') {
          const rawValue: any[] = control.value || [];
          const options = field.options || [];
          const chips = field.withCustomOptions
            ? rawValue
            : rawValue.map((v: any) => options.find((o: any) => o.value === v)).filter(Boolean);
          this.selectedChips.update(s => ({ ...s, [field.key]: chips }));
        }
      } else if (!conditionMet && control.enabled) {
        control.disable({ emitEvent: false });
        if (field.type === 'multiselect') {
          this.selectedChips.update(s => ({ ...s, [field.key]: [] }));
        }
      }
    });
  }

  ngAfterViewInit() {
    this.ready.emit(this);
    this.formChange.emit({
      isValid: this.form.valid,
      value: this.form.value
    });
  }

  ngOnDestroy() {
        if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['catalogs'] || changes['fields']) {
      this.setCatalogs();
    }
    if (
      !this.previewsInitialized &&
      (changes['formData'] || changes['fields']) &&
      this.formData &&
      this.fields?.length
    ) {
      this.initializeFilePreviews();
      this.previewsInitialized = true;
    }
    // if (changes['formData'] && this.form) {
    //    this.form.patchValue(this.formData);
    //    this.initializeMultiselectValues();
    // }
  }

  private setCatalogs() {
    this.fields().forEach(field => {
      if (field.catalog) {
        field.options = this.catalogs[field.catalog];
      }
    });
  }

  initializeFilePreviews() {
    const previews: Record<string, SafeUrl> = {};
    this.fields().forEach(field => {
      if (field.type === 'file') {
        const value = this.formData[field.key];
        // Check if value is the object { id: "...", url: "..." }
        if (value && typeof value === 'object' && value.url) {
          previews[field.key] = this.domSanitizer.bypassSecurityTrustUrl(value.url);
          // Also set the form control value to this object so it's not empty
          if (this.form) {
            this.form.get(field.key)?.setValue(value);
          }
        }
      }
    });
    // Merge with existing previews or overwrite? 
    // Since we are initializing from data, likely want to overwrite or merge.
    // Let's use update to merge safely, although typically this is initial state.
    this.filePreviews.set(previews);
  }

  createForm(): FormGroup {
    const group: Record<string, FormControl<any>> = {};

    this.fields().forEach(field => {
      const validators: ValidatorFn[] = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      if (field.maxLength) {
        validators.push(Validators.maxLength(field.maxLength));
      }

      if (field.maxLength && field.type === 'number') {
        validators.push(maxDigits(field.maxLength));
      }

      if (field.minLength) {
        validators.push(Validators.minLength(field.minLength));
      }

      if (field.minLength && field.type === 'number') {
        validators.push(minDigits(field.minLength));
      }

      if (field.min) {
        validators.push(Validators.min(field.min));
      }

      if (field.max) {
        validators.push(Validators.max(field.max));
      }
      
      if (field.type === 'multiselect') {
        group[field.key] = new FormControl(this.formData[field.key] || [], validators);
      } else if (field.type === 'date' && this.formData[field.key]) {
        const dateValue = new Date(this.formData[field.key]);
        group[field.key] = new FormControl(isNaN(dateValue.getTime()) ? null : dateValue, validators);
      } else if (field.type === 'checkbox') {
        group[field.key] = new FormControl(this.formData[field.key] || false, validators);
      } else {
        group[field.key] = new FormControl(this.formData[field.key] || '', validators);
      }
    });

    return new FormGroup(group);
  }

  initializeMultiselectValues() {
    const chips: Record<string, any[]> = {};
    this.fields().forEach(field => {
      if (field.type === 'multiselect') {
        const values = [];
        for (const value of this.formData[field.key] || []) {
          const findCatalog = field?.options?.find((opt: any) => opt.value === value);
          if (findCatalog) {
            values.push(findCatalog);
          }
        }

        // chips[field.key] = this.formData[field.key] || [];
        if(field.withCustomOptions) {
          chips[field.key] = this.formData[field.key];
        } else {
          chips[field.key] = values;
        }
      }
    });
    this.selectedChips.set(chips);
  }

  onSubmit(isDraft: boolean = false) {
    if (isDraft) {
      // En modo borrador se permiten campos requeridos vacíos, pero el resto
      // de validaciones (min, max, minLength, maxLength, pattern...) siguen aplicando.
      const blockingControls = Object.keys(this.form.controls).filter((key) => {
        const control = this.form.get(key);
        if (!control || control.valid) return false;

        // Si el campo está vacío (length 0), no se toman en cuenta las validaciones
        // de longitud mínima (minlength/minDigits): un campo vacío solo puede faltar.
        const value = control.value;
        const isEmpty =
          value === null ||
          value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);
        const ignoredWhenEmpty = ['requiredError', 'required', 'minlength', 'minDigits'];

        // console.log(`Control ${key} is invalid. Value:`, value, 'Errors:', control.errors, 'isEmpty:', isEmpty);
        return Object.keys(control.errors || {}).some((error) =>
          isEmpty ? !ignoredWhenEmpty.includes(error) : error !== 'required',
        );
      });

      if (blockingControls.length) {
        blockingControls.forEach((key) => this.form.get(key)?.markAsTouched());
        return;
      }
    } else {
      this.form.markAllAsTouched();
      if (!this.form.valid) return;
    }

    const data = this.form.value;
    this.fields().forEach(field => {
      if (field.type === 'file') {
        data[field.key] = data[field.key].id || data[field.key];
      }
    });

    const eventData = {
      isValid: true,
      data: this.form.value,
      isDraft
    };

    this.formSubmit.emit(eventData);
  }

  // Multiselect methods
  onMultiselectSearch(fieldKey: string, event: Event) {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery.update(q => ({ ...q, [fieldKey]: query }));
    this.showDropdown.update(d => ({ ...d, [fieldKey]: true }));
    // Emit through subject for debounce

    const field = this.fields().find(f => f.key === fieldKey);
    if (field?.withCustomOptions) {
      this.searchSubject.next({ fieldKey, query });
    }
  }

  selectOption(fieldKey: string, option: any) {
    const currentChips = this.selectedChips()[fieldKey] || [];
    
    // Check if already selected
    const exists = currentChips.find(chip => chip.value === option.value);
    if (!exists) {
      const updatedChips = [...currentChips, option];
      this.selectedChips.update(chips => ({ ...chips, [fieldKey]: updatedChips }));
    
      if (this.fields().find(f => f.key === fieldKey)?.withCustomOptions) {
        this.form.get(fieldKey)?.setValue(updatedChips);
      } else {
        this.form.get(fieldKey)?.setValue(updatedChips.map(c => c.value));
      }
    }
    
    // Clear search and close dropdown
    this.searchQuery.update(q => ({ ...q, [fieldKey]: '' }));
    this.showDropdown.update(d => ({ ...d, [fieldKey]: false }));
  }

  removeChip(fieldKey: string, chipValue: any) {
    const currentChips = this.selectedChips()[fieldKey] || [];
    const updatedChips = currentChips.filter(chip => chip.value !== chipValue);
    this.selectedChips.update(chips => ({ ...chips, [fieldKey]: updatedChips }));
    this.form.get(fieldKey)?.setValue(updatedChips.map(c => c.value));
  }

  createNewOption(fieldKey: string) {
    const query = this.searchQuery()[fieldKey];
    if (query) {
      this.multiselectCreate.emit({ fieldKey, value: query });
      this.searchQuery.update(q => ({ ...q, [fieldKey]: '' }));
      this.showDropdown.update(d => ({ ...d, [fieldKey]: false }));
    }
  }

  getFilteredOptions(field: FormField): any[] {
    const query = this.searchQuery()[field.key]?.toLowerCase() || '';
    if (!query) return field.options || [];
    return (field.options || []).filter(opt => 
      opt.label.toLowerCase().includes(query)
    );
  }

  // File methods
  async onFileSelect(fieldKey: string, event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    
    if (file) {
      if (file.name?.toLowerCase().endsWith('.jfif')) {
        this.form.get(fieldKey)?.setErrors({ invalidExtension: true });
        this.form.get(fieldKey)?.markAsTouched();
        input.value = '';
        return;
      }

      this.form.get(fieldKey)?.setValue(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        // verify max dimensions
        const maxDimensions = this.fields().find(f => f.key === fieldKey)?.maxDimensions;
        
        // Validate dimensions first if required
        if (maxDimensions) {
          const isValid = await this.validateImageDimensions(file, maxDimensions, fieldKey);
          if (!isValid) {
            // Clear the file input and return early
            // this.form.get(fieldKey)?.setValue(null);
            // input.value = '';
            return;
          }
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.filePreviews.update(previews => ({
            ...previews,
            [fieldKey]: e.target?.result as string
          }));
        };
        reader.readAsDataURL(file);
      }

      // Save file only if validation passed
      const folder = this.fields().find(f => f.key === fieldKey)?.folder || 'users';
      const fileResponse = await this.filesService.saveFileApi(file, folder);
      this.form.get(fieldKey)?.setValue(fileResponse);
    }
  }

  private validateImageDimensions(file: File, maxDimensions: string, fieldKey: string): Promise<boolean> {
    return new Promise((resolve) => {
      const [maxWidth, maxHeight] = maxDimensions.replaceAll(' ', '').split('x').filter(x => !!x).map(
        (dim: string) => parseInt(dim, 10)
      );
      
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {

        if (img.width > maxWidth || img.height > maxHeight) {
          this.form.get(fieldKey)?.setErrors({ maxDimensions: true });
          this.form.get(fieldKey)?.markAsTouched();
          resolve(false);
        } else {
          // Clear any previous dimension errors
          this.form.get(fieldKey)?.setErrors(null);
          resolve(true);
        }
        
        // Clean up object URL
        URL.revokeObjectURL(img.src);
      };
      
      img.onerror = () => {
        console.error('Error loading image for validation');
        resolve(false);
      };
    });
  }

  onFileDeleteClick(fieldKey: string) {
    this.form.get(fieldKey)?.setValue(null);

    // this.form.get(fieldKey)?.updateValueAndValidity();

   

    this.filePreviews.update(previews => {
      const updated = { ...previews };
      delete updated[fieldKey];
      return updated;
    });

    this.fileDelete.emit({ fieldKey });
  }

  // getFilePreview = (fieldKey: string) =>
  //   computed<SafeUrl | null>(() => {
  //     const url = this.filePreviews()[fieldKey];
  //     return url ?? null;
  //   });
  getFilePreview(fieldKey: string): SafeUrl | null {
    return this.filePreviews()[fieldKey] || null;
  }

  showDropdownFor(fieldKey: string) {
    this.showDropdown.update(d => ({ ...d, [fieldKey]: true }));
  }

  focusSearchInput(fieldKey: string) {
    const input = document.getElementById(fieldKey) as HTMLInputElement;
    if (input) {
      input.focus();
    }
  }
}
